import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantsAPI, documentsAPI, conversationsAPI } from '../services/api';

// 위치 옵션
const POSITION_OPTIONS = [
  { value: 'top-left', label: '왼쪽 상단' },
  { value: 'top-center', label: '중앙 상단' },
  { value: 'top-right', label: '오른쪽 상단' },
  { value: 'center-left', label: '왼쪽 중앙' },
  { value: 'center', label: '화면 중앙' },
  { value: 'center-right', label: '오른쪽 중앙' },
  { value: 'bottom-left', label: '왼쪽 하단' },
  { value: 'bottom-center', label: '중앙 하단' },
  { value: 'bottom-right', label: '오른쪽 하단' },
];

// 기본 위젯 설정
const DEFAULT_WIDGET_SETTINGS = {
  chatbot_name: 'AI 상담사',
  welcome_message: '안녕하세요! 무엇을 도와드릴까요?',
  placeholder: '메시지를 입력하세요...',
  primary_color: '#3B82F6',
  secondary_color: '#60A5FA',
  chat_bg_color: '#f8f9fa',
  user_bubble_color: '#3B82F6',
  bot_bubble_color: '#ffffff',
  button_position: 'bottom-right',
  chat_position: 'bottom-right',
  theme: 'light',
};

// 위치 선택 그리드 컴포넌트
function PositionSelector({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
}) {
  const positions = [
    ['top-left', 'top-center', 'top-right'],
    ['center-left', 'center', 'center-right'],
    ['bottom-left', 'bottom-center', 'bottom-right'],
  ];

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className="grid grid-cols-3 gap-1 w-48 p-2 bg-gray-100 rounded-lg">
        {positions.map((row, rowIdx) => (
          <div key={rowIdx} className="contents">
            {row.map((pos) => (
              <button
                key={pos}
                type="button"
                onClick={() => onChange(pos)}
                className={`w-14 h-10 rounded text-xs font-medium transition ${
                  value === pos
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                {POSITION_OPTIONS.find((o) => o.value === pos)?.label.replace(' ', '\n')}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// 색상 입력 컴포넌트
function ColorInput({
  label,
  value,
  onChange,
  description,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  description?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {description && <p className="text-xs text-gray-500 mb-2">{description}</p>}
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded cursor-pointer border border-gray-300"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm"
          placeholder="#000000"
        />
      </div>
    </div>
  );
}

export function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'info' | 'widget' | 'documents' | 'conversations'>('info');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    allowed_domains: '',
    is_active: true,
    plan_id: '',
  });

  // [2026-03-23] 요금제 목록 조회
  const { data: plansData } = useQuery({
    queryKey: ['plans'],
    queryFn: () => tenantsAPI.listPlans(),
  });
  const plans = plansData?.data || [];
  const [widgetSettings, setWidgetSettings] = useState(DEFAULT_WIDGET_SETTINGS);
  const [widgetEditMode, setWidgetEditMode] = useState(false);

  const { data: tenantData, isLoading } = useQuery({
    queryKey: ['tenant', id],
    queryFn: () => tenantsAPI.get(id!),
    enabled: !!id,
  });

  const tenant = tenantData?.data;

  // 테넌트 데이터가 로드되면 위젯 설정 초기화
  useEffect(() => {
    if (tenant?.settings) {
      setWidgetSettings({
        ...DEFAULT_WIDGET_SETTINGS,
        ...tenant.settings,
      });
    }
  }, [tenant]);

  const { data: documentsData } = useQuery({
    queryKey: ['tenant-documents', id],
    queryFn: () => documentsAPI.list(tenant?.api_key, 1, 100),
    enabled: !!tenant?.api_key && activeTab === 'documents',
  });

  const { data: conversationsData } = useQuery({
    queryKey: ['tenant-conversations', id],
    queryFn: () => conversationsAPI.list(tenant?.api_key, 1, 100),
    enabled: !!tenant?.api_key && activeTab === 'conversations',
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => tenantsAPI.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant', id] });
      setEditMode(false);
      setWidgetEditMode(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => tenantsAPI.delete(id!),
    onSuccess: () => {
      navigate('/tenants');
    },
  });

  const regenerateApiKeyMutation = useMutation({
    mutationFn: () => tenantsAPI.regenerateApiKey(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant', id] });
    },
  });

  const regenerateWidgetKeyMutation = useMutation({
    mutationFn: () => tenantsAPI.regenerateWidgetKey(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant', id] });
    },
  });

  const handleStartEdit = () => {
    if (tenant) {
      setFormData({
        name: tenant.name,
        description: tenant.description || '',
        allowed_domains: tenant.allowed_domains?.join(', ') || '',
        is_active: tenant.is_active,
        plan_id: tenant.plan_id || '',
      });
      setEditMode(true);
    }
  };

  const handleSave = () => {
    updateMutation.mutate({
      name: formData.name,
      description: formData.description || null,
      allowed_domains: formData.allowed_domains
        .split(',')
        .map((d) => d.trim())
        .filter(Boolean),
      is_active: formData.is_active,
      plan_id: formData.plan_id || null,
    });
  };

  const handleSaveWidgetSettings = () => {
    updateMutation.mutate({
      settings: widgetSettings,
    });
  };

  const handleStartWidgetEdit = () => {
    if (tenant?.settings) {
      setWidgetSettings({
        ...DEFAULT_WIDGET_SETTINGS,
        ...tenant.settings,
      });
    }
    setWidgetEditMode(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (isLoading) {
    return (
      <div className="p-8 text-center text-gray-500">
        로딩 중...
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="p-8 text-center text-gray-500">
        테넌트를 찾을 수 없습니다.
      </div>
    );
  }

  const documents = documentsData?.data?.items || [];
  const conversations = conversationsData?.data?.items || [];
  const currentSettings = tenant.settings || DEFAULT_WIDGET_SETTINGS;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <button
            onClick={() => navigate('/tenants')}
            className="text-gray-500 hover:text-gray-700 mb-2 flex items-center gap-1"
          >
            ← 목록으로
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{tenant.name}</h1>
          <p className="text-gray-600">{tenant.slug}</p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'info' && (
            <>
              {!editMode ? (
                <>
                  <button
                    onClick={handleStartEdit}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                  >
                    수정
                  </button>
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                  >
                    삭제
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setEditMode(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                  >
                    취소
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={updateMutation.isPending}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
                  >
                    {updateMutation.isPending ? '저장 중...' : '저장'}
                  </button>
                </>
              )}
            </>
          )}
          {activeTab === 'widget' && (
            <>
              {!widgetEditMode ? (
                <button
                  onClick={handleStartWidgetEdit}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  디자인 수정
                </button>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setWidgetEditMode(false);
                      if (tenant?.settings) {
                        setWidgetSettings({
                          ...DEFAULT_WIDGET_SETTINGS,
                          ...tenant.settings,
                        });
                      }
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                  >
                    취소
                  </button>
                  <button
                    onClick={handleSaveWidgetSettings}
                    disabled={updateMutation.isPending}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
                  >
                    {updateMutation.isPending ? '저장 중...' : '저장'}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-8">
          {[
            { key: 'info', label: '기본 정보' },
            { key: 'widget', label: '위젯 디자인' },
            { key: 'documents', label: '문서' },
            { key: 'conversations', label: '대화' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'info' && (
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">기본 정보</h2>
            {editMode ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    테넌트명
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    설명
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    허용 도메인
                  </label>
                  <input
                    type="text"
                    value={formData.allowed_domains}
                    onChange={(e) => setFormData({ ...formData, allowed_domains: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="example.com, localhost (쉼표로 구분)"
                  />
                </div>
                {/* [2026-03-23] 요금제 선택 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">요금제</label>
                  <select
                    value={formData.plan_id}
                    onChange={(e) => setFormData({ ...formData, plan_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value="">선택 안 함</option>
                    {plans.map((p: any) => (
                      <option key={p.id} value={p.id}>
                        {p.display_name} (메시지 {p.max_messages_per_month === -1 ? '무제한' : p.max_messages_per_month}건, 문서 {p.max_documents === -1 ? '무제한' : p.max_documents}건)
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="is_active" className="text-sm text-gray-700">
                    활성화
                  </label>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-500">테넌트명</div>
                  <div className="font-medium">{tenant.name}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">슬러그</div>
                  <div className="font-medium">{tenant.slug}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">상태</div>
                  <span
                    className={`inline-block px-2 py-1 text-xs rounded-full ${
                      tenant.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {tenant.is_active ? '활성' : '비활성'}
                  </span>
                </div>
                <div>
                  <div className="text-sm text-gray-500">요금제</div>
                  <div className="font-medium">
                    {plans.find((p: any) => p.id === tenant.plan_id)?.display_name || '미설정'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">생성일</div>
                  <div className="font-medium">
                    {new Date(tenant.created_at).toLocaleDateString('ko-KR')}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-sm text-gray-500">설명</div>
                  <div className="font-medium">{tenant.description || '-'}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-sm text-gray-500">허용 도메인</div>
                  <div className="font-medium">
                    {tenant.allowed_domains?.length > 0
                      ? tenant.allowed_domains.join(', ')
                      : '-'}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* API Keys */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">API 키</h2>
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-500 mb-1">API Key (관리용)</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-gray-100 rounded font-mono text-sm break-all">
                    {tenant.api_key}
                  </code>
                  <button
                    onClick={() => copyToClipboard(tenant.api_key)}
                    className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded transition"
                  >
                    복사
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('API 키를 재발급하시겠습니까? 기존 키는 더 이상 사용할 수 없습니다.')) {
                        regenerateApiKeyMutation.mutate();
                      }
                    }}
                    disabled={regenerateApiKeyMutation.isPending}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded transition"
                  >
                    재발급
                  </button>
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Widget Key (위젯용)</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-gray-100 rounded font-mono text-sm break-all">
                    {tenant.widget_key}
                  </code>
                  <button
                    onClick={() => copyToClipboard(tenant.widget_key)}
                    className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded transition"
                  >
                    복사
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('위젯 키를 재발급하시겠습니까? 기존 키는 더 이상 사용할 수 없습니다.')) {
                        regenerateWidgetKeyMutation.mutate();
                      }
                    }}
                    disabled={regenerateWidgetKeyMutation.isPending}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded transition"
                  >
                    재발급
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Widget Integration */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">위젯 설치 코드</h2>
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
{`<script
  src="https://your-domain.com/widget/aichat-widget.iife.js"
  data-widget-key="${tenant.widget_key}"
  data-api-url="https://api.your-domain.com"
></script>`}
            </pre>
            <button
              onClick={() => copyToClipboard(`<script\n  src="https://your-domain.com/widget/aichat-widget.iife.js"\n  data-widget-key="${tenant.widget_key}"\n  data-api-url="https://api.your-domain.com"\n></script>`)}
              className="mt-3 px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded transition"
            >
              코드 복사
            </button>
          </div>
        </div>
      )}

      {/* Widget Design Tab */}
      {activeTab === 'widget' && (
        <div className="space-y-6">
          {/* 챗봇 기본 설정 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">챗봇 기본 설정</h2>
            {widgetEditMode ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    챗봇 이름
                  </label>
                  <input
                    type="text"
                    value={widgetSettings.chatbot_name}
                    onChange={(e) => setWidgetSettings({ ...widgetSettings, chatbot_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    환영 메시지
                  </label>
                  <textarea
                    value={widgetSettings.welcome_message}
                    onChange={(e) => setWidgetSettings({ ...widgetSettings, welcome_message: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    입력창 플레이스홀더
                  </label>
                  <input
                    type="text"
                    value={widgetSettings.placeholder}
                    onChange={(e) => setWidgetSettings({ ...widgetSettings, placeholder: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    테마
                  </label>
                  <select
                    value={widgetSettings.theme}
                    onChange={(e) => setWidgetSettings({ ...widgetSettings, theme: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value="light">라이트 모드</option>
                    <option value="dark">다크 모드</option>
                  </select>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-500">챗봇 이름</div>
                  <div className="font-medium">{currentSettings.chatbot_name || 'AI 상담사'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">테마</div>
                  <div className="font-medium">{currentSettings.theme === 'dark' ? '다크 모드' : '라이트 모드'}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-sm text-gray-500">환영 메시지</div>
                  <div className="font-medium">{currentSettings.welcome_message || '안녕하세요! 무엇을 도와드릴까요?'}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-sm text-gray-500">입력창 플레이스홀더</div>
                  <div className="font-medium">{currentSettings.placeholder || '메시지를 입력하세요...'}</div>
                </div>
              </div>
            )}
          </div>

          {/* 위치 설정 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">위치 설정</h2>
            {widgetEditMode ? (
              <div className="flex gap-12">
                <PositionSelector
                  label="채팅 버튼 위치"
                  value={widgetSettings.button_position}
                  onChange={(value) => setWidgetSettings({ ...widgetSettings, button_position: value })}
                />
                <PositionSelector
                  label="채팅창 위치"
                  value={widgetSettings.chat_position}
                  onChange={(value) => setWidgetSettings({ ...widgetSettings, chat_position: value })}
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-500">채팅 버튼 위치</div>
                  <div className="font-medium">
                    {POSITION_OPTIONS.find((o) => o.value === currentSettings.button_position)?.label || '오른쪽 하단'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">채팅창 위치</div>
                  <div className="font-medium">
                    {POSITION_OPTIONS.find((o) => o.value === currentSettings.chat_position)?.label || '오른쪽 하단'}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 색상 설정 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">색상 설정</h2>
            {widgetEditMode ? (
              <div className="grid grid-cols-2 gap-6">
                <ColorInput
                  label="주요 색상"
                  description="헤더, 버튼에 사용됩니다"
                  value={widgetSettings.primary_color}
                  onChange={(value) => setWidgetSettings({ ...widgetSettings, primary_color: value })}
                />
                <ColorInput
                  label="보조 색상"
                  value={widgetSettings.secondary_color}
                  onChange={(value) => setWidgetSettings({ ...widgetSettings, secondary_color: value })}
                />
                <ColorInput
                  label="채팅창 배경 색상"
                  value={widgetSettings.chat_bg_color}
                  onChange={(value) => setWidgetSettings({ ...widgetSettings, chat_bg_color: value })}
                />
                <ColorInput
                  label="사용자 말풍선 색상"
                  value={widgetSettings.user_bubble_color}
                  onChange={(value) => setWidgetSettings({ ...widgetSettings, user_bubble_color: value })}
                />
                <ColorInput
                  label="봇 말풍선 색상"
                  value={widgetSettings.bot_bubble_color}
                  onChange={(value) => setWidgetSettings({ ...widgetSettings, bot_bubble_color: value })}
                />
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: '주요 색상', color: currentSettings.primary_color || '#3B82F6' },
                  { label: '보조 색상', color: currentSettings.secondary_color || '#60A5FA' },
                  { label: '채팅창 배경', color: currentSettings.chat_bg_color || '#f8f9fa' },
                  { label: '사용자 말풍선', color: currentSettings.user_bubble_color || '#3B82F6' },
                  { label: '봇 말풍선', color: currentSettings.bot_bubble_color || '#ffffff' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded border border-gray-300"
                      style={{ backgroundColor: item.color }}
                    />
                    <div>
                      <div className="text-sm text-gray-500">{item.label}</div>
                      <div className="font-mono text-sm">{item.color}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 미리보기 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">미리보기</h2>
            <div
              className="relative border rounded-lg overflow-hidden"
              style={{
                height: '400px',
                backgroundColor: widgetEditMode ? widgetSettings.chat_bg_color : (currentSettings.chat_bg_color || '#f8f9fa'),
              }}
            >
              {/* 채팅창 미리보기 */}
              <div
                className="absolute w-80 rounded-lg shadow-xl overflow-hidden"
                style={{
                  top: '20px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: widgetEditMode
                    ? (widgetSettings.theme === 'dark' ? '#1f2937' : 'white')
                    : (currentSettings.theme === 'dark' ? '#1f2937' : 'white'),
                }}
              >
                {/* 헤더 */}
                <div
                  className="p-4 text-white"
                  style={{
                    backgroundColor: widgetEditMode ? widgetSettings.primary_color : (currentSettings.primary_color || '#3B82F6'),
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
                      </svg>
                    </div>
                    <div>
                      <div className="font-semibold">
                        {widgetEditMode ? widgetSettings.chatbot_name : (currentSettings.chatbot_name || 'AI 상담사')}
                      </div>
                      <div className="text-sm opacity-90">온라인</div>
                    </div>
                  </div>
                </div>
                {/* 메시지 영역 */}
                <div className="p-4 space-y-3" style={{ minHeight: '150px' }}>
                  {/* 환영 메시지 */}
                  <div className="text-center text-sm text-gray-500 py-4">
                    {widgetEditMode ? widgetSettings.welcome_message : (currentSettings.welcome_message || '안녕하세요!')}
                  </div>
                  {/* 봇 메시지 예시 */}
                  <div className="flex gap-2">
                    <div
                      className="px-4 py-2 rounded-2xl rounded-bl-sm max-w-[80%] text-sm"
                      style={{
                        backgroundColor: widgetEditMode ? widgetSettings.bot_bubble_color : (currentSettings.bot_bubble_color || '#ffffff'),
                        border: '1px solid #e5e7eb',
                      }}
                    >
                      무엇을 도와드릴까요?
                    </div>
                  </div>
                  {/* 사용자 메시지 예시 */}
                  <div className="flex justify-end">
                    <div
                      className="px-4 py-2 rounded-2xl rounded-br-sm max-w-[80%] text-sm text-white"
                      style={{
                        backgroundColor: widgetEditMode ? widgetSettings.user_bubble_color : (currentSettings.user_bubble_color || '#3B82F6'),
                      }}
                    >
                      안녕하세요!
                    </div>
                  </div>
                </div>
                {/* 입력창 */}
                <div className="p-3 border-t">
                  <div className="flex items-center gap-2 bg-gray-100 rounded-full px-4 py-2">
                    <input
                      type="text"
                      placeholder={widgetEditMode ? widgetSettings.placeholder : (currentSettings.placeholder || '메시지를 입력하세요...')}
                      className="flex-1 bg-transparent text-sm outline-none"
                      disabled
                    />
                    <button
                      className="w-8 h-8 rounded-full text-white flex items-center justify-center"
                      style={{
                        backgroundColor: widgetEditMode ? widgetSettings.primary_color : (currentSettings.primary_color || '#3B82F6'),
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 향후 기능 안내 */}
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">향후 추가 예정 기능</h3>
            <ul className="text-sm text-gray-500 space-y-1">
              <li>• 말풍선 디자인 (모양, 크기 변경)</li>
              <li>• 커스텀 버튼 이미지 업로드</li>
              <li>• 채팅창 크기 조절</li>
              <li>• 애니메이션 효과 설정</li>
            </ul>
          </div>
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="bg-white rounded-lg shadow">
          {documents.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              등록된 문서가 없습니다.
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    제목
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    타입
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    청크
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    생성일
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {documents.map((doc: any) => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">{doc.title}</td>
                    <td className="px-6 py-4 text-gray-600">{doc.source_type}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          doc.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : doc.status === 'processing'
                            ? 'bg-blue-100 text-blue-800'
                            : doc.status === 'failed'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {doc.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{doc.chunk_count || 0}</td>
                    <td className="px-6 py-4 text-gray-500">
                      {new Date(doc.created_at).toLocaleDateString('ko-KR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'conversations' && (
        <div className="bg-white rounded-lg shadow">
          {conversations.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              대화 기록이 없습니다.
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    세션 ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    메시지 수
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    시작 시간
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    마지막 활동
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {conversations.map((conv: any) => (
                  <tr key={conv.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-mono text-sm">
                      {conv.session_id?.slice(0, 8)}...
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {conv.messages?.length || 0}
                    </td>
                    <td className="px-6 py-4 text-gray-500">
                      {new Date(conv.created_at).toLocaleString('ko-KR')}
                    </td>
                    <td className="px-6 py-4 text-gray-500">
                      {new Date(conv.updated_at).toLocaleString('ko-KR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-semibold mb-4">테넌트 삭제</h2>
            <p className="text-gray-600 mb-6">
              정말로 <strong>{tenant.name}</strong> 테넌트를 삭제하시겠습니까?
              <br />
              이 작업은 되돌릴 수 없으며, 모든 문서와 대화 기록이 삭제됩니다.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
              >
                취소
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition"
              >
                {deleteMutation.isPending ? '삭제 중...' : '삭제'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
